import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getAccountProfile } from '../services/apis/tradingAccountProfile'
import { selectPrimaryTradingAccount, selectTradingAccounts } from '../selectors/user'
import {
    findBesseggenAccount,
    findPracticeAccount,
    getAccountSwitcherMode,
    hasActiveTradingAccount,
    isBesseggenAccountSwitchable
} from '../util/tradingAccountContext'
import { isPracticeTradeAccountEnabled, isRealTradingAccount, isTradingAccountVisibleForBackend } from '../util/tradingAccounts'
import { supportsPracticeTrade } from '../util/backendProvider'

export const useTradingAccountContext = () => {
    const tradingAccounts = useSelector(selectTradingAccounts)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const [profileClientCode, setProfileClientCode] = useState<string>()

    const visibleTradingAccounts = useMemo(
        () => (tradingAccounts ?? []).filter((account) => isTradingAccountVisibleForBackend(account.brokerCode)),
        [tradingAccounts]
    )
    const practiceAccount = useMemo(
        () => supportsPracticeTrade() ? findPracticeAccount(visibleTradingAccounts) : undefined,
        [visibleTradingAccounts]
    )
    const besseggenAccount = useMemo(
        () => findBesseggenAccount(visibleTradingAccounts),
        [visibleTradingAccounts]
    )
    const hasRealAccount = useMemo(
        () => visibleTradingAccounts.some((account) => isRealTradingAccount(account.brokerCode)),
        [visibleTradingAccounts]
    )

    useEffect(() => {
        if (!hasRealAccount) {
            setProfileClientCode(undefined)
            return
        }

        let cancelled = false
        void getAccountProfile()
            .then((profile) => {
                if (!cancelled) {
                    setProfileClientCode(profile.clientCode?.trim() || undefined)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setProfileClientCode(undefined)
                }
            })

        return () => {
            cancelled = true
        }
    }, [hasRealAccount, besseggenAccount?.accountId, besseggenAccount?.status])

    const practiceEnabled = isPracticeTradeAccountEnabled(practiceAccount?.status)
    const besseggenActive = isBesseggenAccountSwitchable(besseggenAccount?.status ?? 0)
    const mode = getAccountSwitcherMode(practiceAccount, besseggenAccount)
    const activeTradingAccount = hasActiveTradingAccount(practiceAccount, besseggenAccount)

    return {
        tradingAccounts: visibleTradingAccounts,
        primaryTradingAccount:
            primaryTradingAccount && isTradingAccountVisibleForBackend(primaryTradingAccount.brokerCode)
                ? primaryTradingAccount
                : visibleTradingAccounts.find((account) => account.primary) ?? visibleTradingAccounts[0],
        practiceAccount,
        besseggenAccount,
        practiceEnabled,
        besseggenActive,
        mode,
        profileClientCode,
        hasAnyAccount: !!practiceAccount || !!besseggenAccount,
        hasActiveTradingAccount: activeTradingAccount
    }
}
